import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "children", "max", "spacing", "edsContext"];
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { default as withStyles } from '../styles';
import { default as MuiAvatarGroup } from '@material-ui/lab/AvatarGroup';
import Avatar from './Avatar';
import List from '../List/List';
import ListItem from '../List/ListItem';
import Popper from '../Popper/Popper';
import Tooltip from '../Tooltip/Tooltip';
import Typography from '../Typography/Typography';
import { withEDSContext } from '../EDSContext/EDSContext';
import classNames from 'classnames';
import { borderWidthThick, borderWidthThin, borderRadiusMedium, spacing20, spacing40, zIndexDefault } from '../styles/tokens';
import MoreHorizontal from '@ellucian/ds-icons/lib/MoreHorizontal';

var styles = function styles(theme) {
  return {
    root: {},
    avatar: {
      backgroundColor: theme.palette.grey['300'],
      fontSize: '1.125rem',
      color: theme.palette.grey['600'],
      border: "solid " + borderWidthThick + " " + theme.palette.grey['100'] + " !important",
      zIndex: zIndexDefault + " !important"
    },
    popper: {
      marginLeft: "-" + spacing40
    },
    paper: {
      border: borderWidthThin + " solid " + theme.palette.grey['400'],
      borderRadius: borderRadiusMedium
    },
    popperList: {
      borderRadius: borderRadiusMedium
    },
    popperItem: {
      '&:first-child': {
        borderRadius: borderRadiusMedium + " " + borderRadiusMedium + " 0px 0px"
      },
      '&:last-child': {
        borderRadius: "0px 0px " + borderRadiusMedium + " " + borderRadiusMedium
      },
      '& $popItemText': {
        color: theme.palette.action.base,
        textDecoration: 'underline'
      },
      '&:hover': {
        '& $popItemText': {
          color: theme.palette.action.hover,
          textDecoration: 'underline',
          cursor: 'pointer'
        }
      },
      '&:active': {
        '& $popItemText': {
          color: theme.palette.grey[100],
          textDecoration: 'underline'
        }
      }
    },
    popperItemAvatar: {
      marginRight: spacing20
    },
    noHover: {
      pointerEvents: 'none'
    },
    popperItemText: {}
  };
};

var TERTIARY_COLORS_MAP = ['purple', 'iris', 'fountain', 'meadow', 'kiwi', 'saffron', 'tangerine'];

var getColors = function getColors(children) {
  var availableColors = TERTIARY_COLORS_MAP.slice(0);
  var finalColors = [];

  for (var i = 0; i < children.length; i++) {
    if (availableColors.length === 0) {
      availableColors = TERTIARY_COLORS_MAP.slice(0);
    }

    ;
    var num = Math.floor(Math.random() * availableColors.length);
    finalColors.push(availableColors[num]);
    availableColors.splice(num, 1);
  }

  ;
  return finalColors;
};

var AvatarGroup = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var classes = props.classes,
      children = props.children,
      max = props.max,
      spacing = props.spacing,
      edsContext = props.edsContext,
      rest = _objectWithoutPropertiesLoose(props, _excluded);

  var newSpacing = spacing === 'condensed' ? 30 : spacing;

  var _useState = useState(),
      anchorEl = _useState[0],
      setAnchorEl = _useState[1];

  var _useState2 = useState(false),
      open = _useState2[0],
      setOpen = _useState2[1];

  var _useState3 = useState(getColors(children)),
      colorList = _useState3[0];

  var classOverrides = {
    root: classes.root,
    avatar: classes.avatar
  };
  var colorizedChildren = children.slice(0).map(function (child, i) {
    return /*#__PURE__*/React.cloneElement(child, {
      color: colorList[i]
    });
  }); // Updates the Avatar components with the given set of prop overrides

  var updateAvatar = function updateAvatar(node, overrides) {
    // if this is an avatar, apply the overrides
    if (node && node.type && node.type.muiName === 'Avatar') {
      return /*#__PURE__*/React.cloneElement(node, overrides); // delve through the children, looking for an Avatar
    } else if (node.props && node.props.children) {
      var clonedChildren = React.Children.map(node.props.children, function (c) {
        return updateAvatar(c, overrides);
      });
      return /*#__PURE__*/React.cloneElement(node, undefined, clonedChildren.length === 1 ? clonedChildren[0] : clonedChildren); // we're at a non-Avatar leaf node; return
    } else {
      return node;
    }
  }; // adds keys to all of the group children; for condensed groups, modify the
  // individual avatars to render them inert


  var groupChildren = colorizedChildren.slice(0, max).map(function (child, i) {
    var updatedAvatar;

    if (spacing === 'condensed') {
      //set elements in the condensed avatar group to be non-interactive
      updatedAvatar = updateAvatar(child, {
        component: 'div',
        variant: 'default',
        className: classNames(child.props.className, classes.noHover),
        // render the avatars inert by swallowing any click events; in condensed
        // view, all avatar interaction will be via the popup menu
        onClick: function onClick(e) {
          e.stopPropagation();
          e.preventDefault();
        }
      });
    } else {
      updatedAvatar = child;
    }

    return /*#__PURE__*/React.cloneElement(updatedAvatar, {
      key: "Group_Avatar_" + i
    });
  });
  var popperChildren = spacing === 'condensed' ? colorizedChildren.slice(0).map(function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      size: 'small',
      tabIndex: '-1'
    });
  }) : colorizedChildren.slice(max, colorizedChildren.length).map(function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      size: 'small',
      tabIndex: '-1'
    });
  });

  var handleClick = function handleClick(event) {
    var currentTarget = event.currentTarget;
    setAnchorEl(currentTarget);
    setOpen(!open);
  };

  var handleClose = function handleClose() {
    setOpen(false);
  }; //synthetically clicks avatar on list item click


  var handleListItemClick = function handleListItemClick(e) {
    var listItem = e.currentTarget;
    listItem.querySelector('.liClickTarget').children[0].click();
  }; // find the avatar in the DOM tree rooted at the given child; if none is
  // found, returns undefined


  var findAvatar = function findAvatar(node) {
    if (node && node.type && node.type.muiName === 'Avatar') {
      return node;
    } else if (node.props && node.props.children) {
      var ac = React.Children.toArray(node.props.children);

      for (var i = 0; i < ac.length; i++) {
        var avatar = findAvatar(ac[i]);

        if (avatar !== undefined) {
          return avatar;
        }
      }
    } else {
      return undefined;
    }
  }; // returns true if the given node is (or contains) an interactive avatar


  var isInteractiveAvatar = function isInteractiveAvatar(node) {
    var testChild = findAvatar(node);
    return testChild !== undefined && testChild.props.variant === 'interactive';
  }; //add terminal avatar


  if (colorizedChildren.length > max) {
    groupChildren.push( /*#__PURE__*/React.createElement(Tooltip, {
      key: "Terminal_Group_Avatar",
      title: edsContext.formatMessage('component.AvatarGroup.openMenu')
    }, /*#__PURE__*/React.createElement(Avatar, {
      variant: 'interactive',
      onClick: handleClick
    }, spacing === 'condensed' ? /*#__PURE__*/React.createElement(MoreHorizontal, null) : '+' + popperChildren.length)));
  }

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(MuiAvatarGroup, _extends({}, rest, {
    classes: classOverrides,
    ref: ref,
    max: 50,
    spacing: newSpacing
  }), groupChildren), /*#__PURE__*/React.createElement(Popper, {
    role: "alertdialog",
    open: open,
    anchorEl: anchorEl,
    placement: edsContext.dir === 'rtl' ? 'left-start' : 'right-start',
    arrow: true,
    onClickAway: handleClose,
    disablePortal: true,
    className: classes.popper,
    PaperProps: {
      classes: {
        root: classes.paper
      }
    }
  }, /*#__PURE__*/React.createElement(List, {
    className: classes.popperList
  }, popperChildren.map(function (child, i) {
    return /*#__PURE__*/React.createElement(ListItem, {
      key: "Popper_Avatar_" + i,
      button: isInteractiveAvatar(child),
      "aria-label": child.props.alt ? child.props.alt : child.props.title,
      onClick: handleListItemClick,
      className: classes.popperItem
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(classes.popperItemAvatar, 'liClickTarget')
    }, child), /*#__PURE__*/React.createElement(Typography, {
      className: child.props.onClick ? classes.popperItemText : null
    }, child.props.alt ? child.props.alt : child.props.title));
  }))));
});
AvatarGroup.muiName = 'AvatarGroup';
AvatarGroup.displayName = 'AvatarGroup';
AvatarGroup.propTypes = {
  /**
   * The avatars to stack.
   */
  children: PropTypes.node,

  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,

  /**
   * Max avatars to show before +x.
   * * Value should be greater than or equal to two.
  */
  max: PropTypes.number,

  /**
   * Spacing between avatars.
   *
   * Valid values:
   *
   * * "medium": Default spacing
   * * "small": Closely-spaced avatars
   * * "condensed": Avatars are stacked, one on top of the other. In this mode, a popup shows all available avatars.
   * * [number]: Positive values decrease the space between avatars; negative values decreate it
   */
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['medium', 'small', 'condensed'])])
};
AvatarGroup.defaultProps = {
  spacing: 'medium',
  max: 4
};
export default withEDSContext(withStyles(styles)(AvatarGroup));